import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Plus, Trash2, ChevronDown } from "lucide-react";
import { showError, showSuccess } from "../../utils/toastUtils";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import useNumericInput from "../../hooks/useNumericInput";
import { containerVariants, itemVariants } from "./variants";
import addressAPI from "../../api/addressApi";

const INITIAL_FORM_DATA = {
  firstName: "",
  lastName: "",
  addressType: "home",
  addressLine1: "",
  addressLine2: "",
  pinCode: "",
  district: "",
  state: "",
  country: "",
};

// Same flow as sanwariyaLatest/src/pages/user/Addresses.js:
// addressAPI.getAllAddress() / addNewAddress() / deleteAddress()
function Addresses() {
  useAlphabeticInput();
  useNumericInput();

  const [isOpen, setIsOpen] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchAddress();
  }, []);

  const fetchAddress = async () => {
    setLoading(true);
    try {
      const res = await addressAPI.getAllAddress();
      if (res.code === 200) {
        setAddresses(res.data || []);
      }
    } catch (err) {
      // silently ignore
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName) newErrors.firstName = "Please Enter First Name";
    if (!formData.addressType) newErrors.addressType = "Please Enter Address Type";
    if (!formData.addressLine1) newErrors.addressLine1 = "Please Enter Address";
    if (!formData.pinCode) newErrors.pinCode = "Please Enter PinCode";
    else if (formData.pinCode.length !== 6) newErrors.pinCode = "PinCode must be of 6 digits";
    if (!formData.district) newErrors.district = "Please Enter District";
    if (!formData.state) newErrors.state = "Please Enter State";
    if (!formData.country) newErrors.country = "Please Enter Country";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const res = await addressAPI.addNewAddress(formData);
      if (res.code === 200) {
        showSuccess(res.message);
        setFormData(INITIAL_FORM_DATA);
        setIsOpen(false);
        fetchAddress();
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteAddress = async (id) => {
    try {
      const res = await addressAPI.deleteAddress(id);
      if (res.code === 200) {
        fetchAddress();
      }
    } catch (err) {
      showError(err.message);
    }
  };

  const radioOptions = [
    { value: "home", label: "Home" },
    { value: "work", label: "Office" },
    { value: "other", label: "Other" },
  ];

  return (
    <motion.div
      key="addresses"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit={{ opacity: 0, x: 14 }}
      className="flex flex-col gap-3.5"
    >
      <div className="mb-1">
        <span className="block mb-0.5 font-subheading text-[0.54rem] font-bold uppercase tracking-[0.2em] text-[var(--color-text-muted)]">
          Manage
        </span>
        <h2 className="font-heading text-[1.7rem] leading-none tracking-[-0.02em] text-[var(--color-text)]">
          My Addresses ({addresses.length})
        </h2>
      </div>

      {/* Add new address accordion */}
      <motion.div variants={itemVariants} className="acc-card overflow-hidden">
        <button
          type="button"
          onClick={() => setIsOpen((v) => !v)}
          className="flex w-full items-center justify-between px-[18px] py-3.5 font-subheading text-[0.63rem] font-semibold uppercase tracking-[0.12em] text-[var(--color-text)]"
        >
          <span className="flex items-center gap-2">
            <Plus size={14} className="text-[var(--color-primary)]" /> Add New Address
          </span>
          <ChevronDown
            size={14}
            className={`text-[var(--color-text-muted)] transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        {isOpen && (
          <form onSubmit={handleSubmit} className="border-t border-[var(--color-border)] px-[18px] py-4">
            <div className="mb-3 grid grid-cols-2 gap-3">
              <div>
                <input
                  className="tf-field-input tf-input only-alphabets w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] px-2.5 py-2 font-body text-[0.9rem]"
                  type="text"
                  name="firstName"
                  placeholder="First Name"
                  value={formData.firstName}
                  onChange={handleChange}
                />
                {errors.firstName && <span className="text-danger text-[0.7rem]">{errors.firstName}</span>}
              </div>
              <input
                className="tf-field-input tf-input only-alphabets w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] px-2.5 py-2 font-body text-[0.9rem]"
                type="text"
                name="lastName"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleChange}
              />
            </div>

            <div className="mb-3 flex flex-wrap gap-4">
              {radioOptions.map((opt) => (
                <label key={opt.value} className="flex cursor-pointer items-center gap-1.5 font-body text-[0.85rem] text-[var(--color-text)]">
                  <input
                    type="radio"
                    name="checkshipping"
                    value={opt.value}
                    checked={formData.addressType === opt.value}
                    onChange={() => setFormData((prev) => ({ ...prev, addressType: opt.value }))}
                    className="accent-[var(--color-primary)]"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
            {errors.addressType && <span className="text-danger text-[0.7rem]">{errors.addressType}</span>}

            <div className="mb-3">
              <input
                className="tf-field-input tf-input w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] px-2.5 py-2 font-body text-[0.9rem]"
                type="text"
                name="addressLine1"
                placeholder="Address Line 1"
                value={formData.addressLine1}
                onChange={handleChange}
              />
              {errors.addressLine1 && <span className="text-danger text-[0.7rem]">{errors.addressLine1}</span>}
            </div>

            <div className="mb-3">
              <input
                className="tf-field-input tf-input w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] px-2.5 py-2 font-body text-[0.9rem]"
                type="text"
                name="addressLine2"
                placeholder="Address Line 2 (Optional)"
                value={formData.addressLine2}
                onChange={handleChange}
              />
            </div>

            <div className="mb-3 grid grid-cols-3 gap-3">
              <div>
                <input
                  className="tf-field-input tf-input only-numeric w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] px-2.5 py-2 font-body text-[0.9rem]"
                  type="text"
                  maxLength={6}
                  name="pinCode"
                  placeholder="Pin Code"
                  value={formData.pinCode}
                  onChange={handleChange}
                />
                {errors.pinCode && <span className="text-danger text-[0.7rem]">{errors.pinCode}</span>}
              </div>
              <div>
                <input
                  className="tf-field-input tf-input only-alphabets w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] px-2.5 py-2 font-body text-[0.9rem]"
                  type="text"
                  name="district"
                  placeholder="District"
                  value={formData.district}
                  onChange={handleChange}
                />
                {errors.district && <span className="text-danger text-[0.7rem]">{errors.district}</span>}
              </div>
              <div>
                <input
                  className="tf-field-input tf-input only-alphabets w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] px-2.5 py-2 font-body text-[0.9rem]"
                  type="text"
                  name="state"
                  placeholder="State"
                  value={formData.state}
                  onChange={handleChange}
                />
                {errors.state && <span className="text-danger text-[0.7rem]">{errors.state}</span>}
              </div>
            </div>

            <div className="mb-4">
              <input
                className="tf-field-input tf-input only-alphabets w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] px-2.5 py-2 font-body text-[0.9rem]"
                type="text"
                name="country"
                placeholder="Country"
                value={formData.country}
                onChange={handleChange}
              />
              {errors.country && <span className="text-danger text-[0.7rem]">{errors.country}</span>}
            </div>

            <button type="submit" disabled={saving} className="acc-btn-p disabled:cursor-not-allowed disabled:opacity-60">
              {saving ? "Saving..." : "Submit"}
            </button>
          </form>
        )}
      </motion.div>

      {/* Address list */}
      {loading ? (
        <p className="font-body text-[0.85rem] text-[var(--color-text-muted)]">Loading addresses…</p>
      ) : addresses.length > 0 ? (
        <div className="grid grid-cols-2 gap-3.5">
          {addresses.map((address, index) => (
            <motion.div key={address._id || index} variants={itemVariants} className="acc-card px-4 py-3.5">
              <div className="mb-2 flex items-center justify-between">
                <span className="flex items-center gap-1.5 font-subheading text-[0.6rem] font-bold uppercase tracking-[0.12em] text-[var(--color-primary)]">
                  <MapPin size={13} /> {address.addressType || "Address"}
                </span>
                <button
                  type="button"
                  onClick={() => deleteAddress(address._id)}
                  className="cursor-pointer text-[var(--color-text-muted)] hover:text-[#c0392b]"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <p className="mb-1 font-body text-[0.9rem] font-semibold text-[var(--color-text)]">
                {address.firstName} {address.lastName}
              </p>
              <p className="font-body text-[0.8rem] leading-[1.5] text-[var(--color-text-muted)]">
                {address.addressLine1}
                {address.addressLine2 ? `, ${address.addressLine2}` : ""}, {address.district}, {address.state} - {address.pinCode}
                <br />
                {address.country}
              </p>
            </motion.div>
          ))}
        </div>
      ) : (
        <p className="font-body text-[0.85rem] text-[var(--color-text-muted)]">No addresses saved yet.</p>
      )}
    </motion.div>
  );
}

export default Addresses;